<template>
  <div
    v-if="condition?.outcome"
    class="flex cursor-default select-none items-center justify-between gap-x-4 p-3"
    :class="`${hoverClass} opacity-100`"
  >
    <div class="flex items-center gap-x-4">
      <slot name="handle"></slot>
      <div
        class="flex h-[56px] w-[56px] items-center justify-center rounded-full"
        :class="iconBgClass"
      >
        <slot></slot>
      </div>
      <div class="flex w-[32rem] flex-col">
        <div :class="`text-sm font-semibold leading-5 ${textClass}`">
          {{ showButtonText(condition) }}
        </div>
        <div
          v-for="(conditionItem, index) in condition?.conditions.slice(0, 5)"
          :key="index"
          class="text-xs leading-5 text-gray-600"
        >
          <span :class="`uppercase ${textClass}`">{{
            index === 0
              ? t("form.conditonalLogic.logic.if")
              : t(
                  `form.conditonalLogic.logic.${(
                    condition.conditionalOperation || ""
                  ).toLowerCase()}`
                )
          }}</span>
          {{
            isSlideCondition(conditionItem)
              ? formatSlideLabel(conditionItem.selectedField)
              : getFieldName(conditionItem.selectedField)
          }}
          <span v-if="!isSlideCondition(conditionItem)" :class="`uppercase ${textClass} mr-1`">{{
            camelToPascal(conditionItem.selectedOperation)
          }}</span>
          <span
            v-if="
              !isSlideCondition(conditionItem) &&
              ![IS_EMPTY, IS_FILLED].includes(conditionItem.selectedOperation)
            "
          >
            <template v-if="conditionItem.selectedOperation === 'between'">
              {{ getBetweenValues(conditionItem).start }}
              <span :class="`uppercase ${textClass} mx-1`">{{
                t("form.conditonalLogic.logic.and")
              }}</span>
              {{ getBetweenValues(conditionItem).end }}
            </template>
            <template v-else>
              {{ formatPlainInputValue(conditionItem) }}
            </template>
          </span>
        </div>
        <div v-if="condition?.conditions.length > 5" class="text-xs leading-5 text-gray-600">
          ...and <span :class="textClass">{{ condition.conditions.length - 5 }}</span> other
          <span :class="textClass">IF</span> rules ...
        </div>
        <div class="flex text-xs leading-5 text-gray-600">
          <span :class="`uppercase ${textClass} min-w-fit`">
            {{ outcomeText(condition) }}
            &nbsp;</span
          >
          <span v-html="showOutcomeValue(condition)"></span>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-x-4">
      <UIButton id="edit-condition" text size="small" class="text-gray-800" @click="handleEdit">
        <Edit01Icon class="h-5 w-5" />
      </UIButton>
      <UIButton id="delete-condition" text size="small" class="text-gray-800" @click="handleDelete">
        <Trash01Icon class="h-5 w-5" />
      </UIButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import {
  colorBgHoverVariants,
  colorBgVariants,
  colorTextVariants,
  IS_EMPTY,
  IS_FILLED,
} from "@/util/constants";
import { camelToPascal } from "@/util/methods";
import { Edit01Icon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  color: {
    type: String,
    default: "gray",
  },
  buttonText: {
    type: String,
    default: "Default Text",
  },
  outcomeType: { type: String, default: "Show/Hide Fields" },
  condition: {
    type: Object,
    default: null,
  },
});

const store = useAppStore();
const emit = defineEmits(["onEditButtonClick", "onDeleteButtonClick"]);
const { t } = useI18n();

const iconBgClass = ref(colorBgVariants[props.color]);
const hoverClass = ref(colorBgHoverVariants[props.color]);
const textClass = ref(colorTextVariants[props.color]);

watchEffect(() => {
  iconBgClass.value = colorBgVariants[props.color];
  hoverClass.value = colorBgHoverVariants[props.color];
  textClass.value = colorTextVariants[props.color];
});

const truncate = (s: string, max = 32) => (s && s.length > max ? s.slice(0, max) + "…" : s);

const toPlain = (s: string) => (s || "").replace(/<\/?[^>]+(>|$)/g, "");

const getFieldName = (hiddenFieldQueryKey) => {
  if (!hiddenFieldQueryKey) {
    return "";
  }

  // Handle split Terms & Conditions keys like "terms_and_conditions_1" / "terms_and_conditions_2"
  const tncMatch = String(hiddenFieldQueryKey).match(/^(.*)_(1|2)$/);
  const tncBaseKey = tncMatch ? tncMatch[1] : null;
  const tncVariantIndex = tncMatch ? tncMatch[2] : null;

  // For forms
  if (store.formId) {
    const directRaw =
      store.elements.find((element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey)
        ?.label ||
      store.addressSettings.children.find(
        (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
      )?.label;
    const direct = truncate(toPlain(String(directRaw || "")));
    if (direct) {
      return direct;
    }
    if (tncBaseKey) {
      const baseEl =
        store.elements.find((e) => e.hiddenFieldQueryKey === tncBaseKey) ||
        store.addressSettings.children.find((e) => e.hiddenFieldQueryKey === tncBaseKey);
      if (baseEl && baseEl.type === "terms_and_conditions") {
        return tncVariantIndex === "1" ? t("common.tnc1") : t("common.tnc2");
      }
    }
    return "";
  }

  // For surveys
  if (store.surveyId) {
    for (const slide of store.slides) {
      if (slide.slideData && Array.isArray(slide.slideData)) {
        const field = slide.slideData.find(
          (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
        );
        if (field) {
          return truncate(toPlain(String(field.label || "")));
        }
        if (tncBaseKey) {
          const baseField = slide.slideData.find(
            (element) => element.hiddenFieldQueryKey === tncBaseKey
          );
          if (baseField && baseField.type === "terms_and_conditions") {
            return tncVariantIndex === "1" ? t("common.tnc1") : t("common.tnc2");
          }
        }
      }
    }
  }

  return (
    truncate(
      toPlain(
        String(
          store.elements.find((element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey)
            ?.label ||
            store.addressSettings?.children?.find(
              (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
            )?.label ||
            ""
        )
      )
    ) ||
    (tncBaseKey &&
    (store.elements.find((e) => e.hiddenFieldQueryKey === tncBaseKey)?.type ===
      "terms_and_conditions" ||
      store.addressSettings?.children?.find((e) => e.hiddenFieldQueryKey === tncBaseKey)?.type ===
        "terms_and_conditions")
      ? tncVariantIndex === "1"
        ? t("common.tnc1")
        : t("common.tnc2")
      : "")
  );
};

const handleEdit = () => emit("onEditButtonClick");

const handleDelete = () => emit("onDeleteButtonClick");

const outcomeText = (condition) => {
  const outcome = condition?.outcome;
  return !outcome
    ? ""
    : outcome.type === "redirectToUrl" ||
      (outcome.type === "disqualifyLead" && outcome.disqualifyAction === "openUrl")
    ? "Open URL"
    : outcome.type === "displayCustomMessage" ||
      (outcome.type === "disqualifyLead" && outcome.disqualifyAction === "showCustomMessage")
    ? "SHOW"
    : outcome.type === "showHideFields"
    ? outcome.hideType
    : outcome.type === "jumpTo"
    ? "JUMP TO"
    : "";
};

const showOutcomeValue = (condition) => {
  const { outcome } = condition;
  if (outcome.type === "showHideFields" && outcome?.hideType.includes("Multiple")) {
    const fieldLabels = outcome.value.map((queryKey) => getFieldName(queryKey)).filter(Boolean);

    if (fieldLabels.length > 5) {
      const shown = fieldLabels.slice(0, 5).join(", ");
      const remaining = fieldLabels.length - 5;
      return `${shown}, +${remaining} more`;
    }
    return fieldLabels.join(", ");
  }

  if (outcome.type === "jumpTo" && outcome.jumpTarget) {
    if (outcome.jumpTarget === "submitSurvey") {
      return t("form.conditonalLogic.submitSurvey") || "Submit Survey";
    }
    const slideIndex = store.slides.findIndex((slide) => slide.id === outcome.jumpTarget);
    if (slideIndex !== -1) {
      return t("form.conditonalLogic.pageTitle", {
        index: slideIndex + 1,
        title: store.slides[slideIndex].slideName,
      });
    }
    return t("form.conditonalLogic.selectedSlide");
  }

  return getFieldName(outcome.value) || outcome.value;
};

const showButtonText = (condition) => {
  const outcome = condition?.outcome;
  if (outcome?.type === "showHideFields") {
    switch (outcome?.hideType) {
      case "Hide Multiple": {
        return "Hide Fields";
      }
      case "Show Multiple": {
        return "Show Fields";
      }
      case "Hide": {
        return "Hide Field";
      }
      case "Show": {
        return "Show Field";
      }
      default: {
        return "";
      }
    }
  } else if (outcome?.type === "jumpTo") {
    return t("form.conditonalLogic.jumpToSlide");
  } else {
    return props.buttonText;
  }
};

// Plain value formatters (no HTML)
const getBetweenValues = (conditionItem: {
  inputValue: unknown;
}): { start: string; end: string } => {
  let payload: unknown = conditionItem?.inputValue;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return { start: String(conditionItem?.inputValue ?? ""), end: "" };
    }
  }
  const start = (payload as any)?.start ?? "";
  const end = (payload as any)?.end ?? "";
  return { start: String(start), end: String(end) };
};

const formatPlainInputValue = (conditionItem: { inputValue: unknown }): string => {
  const val = conditionItem?.inputValue;
  return typeof val === "string" ? val : val != null ? String(val) : "";
};

// Identify slide-based IF rows (Jump To slide mode)
const isSlideCondition = (
  conditionItem: { selectedField?: string; fieldMode?: string } | null | undefined
): boolean => {
  if (!conditionItem) {
    return false;
  }
  if (conditionItem?.fieldMode === "slide") {
    return true;
  }
  if (!store.surveyId) {
    return false;
  }
  return store.slides.some((s) => s.id === conditionItem?.selectedField);
};

const formatSlideLabel = (id: string) => {
  if (!id || !store.surveyId) {
    return "";
  }
  if (id === "submitSurvey") {
    return t("form.conditonalLogic.submitSurvey") || "Submit Survey";
  }
  const index = store.slides.findIndex((s) => s.id === id);
  if (index === -1) {
    return "";
  }
  return t("form.conditonalLogic.pageTitle", {
    index: index + 1,
    title: store.slides[index].slideName,
  });
};
</script>
